import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { IAdminOrganization, createOrganization } from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';

interface Props {
	open: boolean;
	onClose: () => void;
}

export default function CreateOrganizationDialog({ open, onClose }: Props) {
	const queryClient = useQueryClient();
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [nameError, setNameError] = useState<string | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [created, setCreated] = useState<IAdminOrganization | null>(null);

	function resetAndClose() {
		setName('');
		setDescription('');
		setNameError(null);
		setError(null);
		setCreated(null);
		onClose();
	}

	async function handleSubmit() {
		if (!name.trim()) {
			setNameError('Required');
			return;
		}
		setSubmitting(true);
		setError(null);
		try {
			const organization = await createOrganization({
				name: name.trim(),
				description: description.trim() || null,
			});
			await queryClient.invalidateQueries(['admin', 'organizations']);
			setCreated(organization);
		} catch (e: any) {
			setError(apiErrorMessage(e, 'An unexpected error occurred. Please try again.'));
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{created ? (
				<>
					<DialogTitle>Organization Created</DialogTitle>
					<DialogContent>
						<Alert severity="success">
							<strong>{created.name}</strong> was created. You can now add teams to it from the Teams tab.
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button variant="contained" onClick={resetAndClose}>
							Done
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>New Organization</DialogTitle>
					<DialogContent sx={{ pb: 1 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							An organization is a client firm. Teams are created inside an organization and users are
							assigned to teams.
						</Typography>
						<Stack spacing={2}>
							<TextField
								label="Organization Name"
								size="small"
								autoFocus
								required
								value={name}
								onChange={(e) => {
									setName(e.target.value);
									setNameError(null);
								}}
								error={!!nameError}
								helperText={nameError}
								onKeyDown={(e) => {
									if (e.key === 'Enter') handleSubmit();
								}}
							/>
							<TextField
								label="Description (optional)"
								size="small"
								multiline
								minRows={2}
								value={description}
								onChange={(e) => setDescription(e.target.value)}
							/>
						</Stack>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose} disabled={submitting}>
							Cancel
						</Button>
						<Button
							variant="contained"
							onClick={handleSubmit}
							disabled={submitting || !name.trim()}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Creating…' : 'Create Organization'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
